/**
 * PresentationDashboard — decks + folders + templates.
 * Route: /app/presentations
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Sparkles, Trash2, Copy, Download, Presentation as PresIcon,
  Calendar, FileText, Folder, FolderPlus, LayoutGrid, ChevronRight,
} from "lucide-react";
import { MiniSlidePreview } from "@/components/MiniSlidePreview";

export default function PresentationDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [themesById, setThemesById] = useState({});
  const [activeFolder, setActiveFolder] = useState("all"); // "all" | "uncat" | folder_id
  const [loading, setLoading] = useState(true);
  const [tplBusy, setTplBusy] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);

  const load = async () => {
    try {
      const [{ data: p }, { data: f }, { data: t }, { data: cfg }] = await Promise.all([
        api.get("/presentations"),
        api.get("/presentation-folders"),
        api.get("/presentations/templates"),
        api.get("/presentations/config"),
      ]);
      setItems(p.presentations || []);
      setFolders(f.folders || []);
      setTemplates(t.templates || []);
      const map = {};
      (cfg.themes || []).forEach((th) => { map[th.id] = th; });
      setThemesById(map);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this presentation?")) return;
    await api.delete(`/presentations/${id}`);
    load();
  };
  const duplicate = async (id) => {
    const { data } = await api.post(`/presentations/${id}/duplicate`);
    toast.success("Duplicated");
    nav(`/app/presentations/${data.id}/edit`);
  };
  const download = async (id, title) => {
    try {
      const res = await api.get(`/presentations/${id}/export/pptx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(title || "presentation").replace(/[^a-z0-9-]+/gi, "-")}.pptx`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };
  const assignFolder = async (id, folder_id) => {
    try {
      await api.post(`/presentations/${id}/folder`, { folder_id });
      toast.success("Moved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const createFolder = async () => {
    const name = window.prompt("Folder name:");
    if (!name) return;
    try {
      await api.post("/presentation-folders", { name, color: "#7C3AED" });
      toast.success("Folder created");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const delFolder = async (fid) => {
    if (!window.confirm("Delete folder? (decks will move to Uncategorised)")) return;
    try {
      await api.delete(`/presentation-folders/${fid}`);
      toast.success("Folder deleted");
      if (activeFolder === fid) setActiveFolder("all");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const cloneTemplate = async (tid, name) => {
    setTplBusy(tid);
    try {
      const { data } = await api.post(`/presentations/from-template/${tid}`, { title: name });
      toast.success("Template cloned");
      nav(`/app/presentations/${data.id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setTplBusy(""); }
  };

  const filteredItems = items.filter((p) => {
    if (activeFolder === "all") return true;
    if (activeFolder === "uncat") return !p.folder_id;
    return p.folder_id === activeFolder;
  });

  return (
    <div className="space-y-6 vp-fade-up">
      <section
        className="vp-glass p-6 sm:p-8 rounded-2xl flex items-center justify-between gap-4 flex-wrap"
        style={{ background: "linear-gradient(135deg,#EEF2FF 0%,#F5F3FF 50%,#FCE7F3 100%)" }}
      >
        <div>
          <h1 className="font-display font-black text-2xl sm:text-3xl">AI Presentations</h1>
          <p className="text-[#4A4D57] mt-1 text-sm inline-flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#7C3AED]" />
            Templates, folders, version history, brand kits — all in one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowTemplates((v) => !v)} className="vp-btn-ghost text-sm py-2 px-3 inline-flex items-center gap-1.5" data-testid="toggle-templates">
            <LayoutGrid className="w-4 h-4" /> Templates
          </button>
          <button onClick={() => nav("/app/presentations/new")} className="vp-btn-primary inline-flex items-center gap-2" data-testid="new-presentation-btn">
            <Plus className="w-4 h-4" /> New Presentation
          </button>
        </div>
      </section>

      {showTemplates && (
        <section className="vp-card p-4 space-y-3" data-testid="templates-grid">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-lg">Start from a template</div>
              <div className="text-xs text-[#4A4D57]">Professionally crafted decks — clone and customize.</div>
            </div>
            <button onClick={() => setShowTemplates(false)} className="text-xs text-[#747680] hover:underline">Hide</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {templates.map((t) => {
              const theme = themesById[t.theme_id] || { primary: t.cover_color, secondary: "#4A4D57", accent: "#F59E0B", bg: "#FFFFFF", fg: "#0B0D17" };
              return (
                <button
                  key={t.id}
                  onClick={() => cloneTemplate(t.id, t.name)}
                  disabled={tplBusy === t.id}
                  className="vp-card p-2 text-left hover:border-[#7C3AED] transition disabled:opacity-50"
                  data-testid={`template-${t.id}`}
                >
                  <div className="rounded overflow-hidden mb-2 border border-[#E4E6EC]">
                    <MiniSlidePreview slide={t.slides[0]} theme={theme} />
                  </div>
                  <div className="font-display font-semibold text-sm truncate">{t.name}</div>
                  <div className="text-[10px] text-[#747680] mt-0.5 flex items-center justify-between">
                    <span>{t.category}</span>
                    <span className="text-[9px]">{t.slides.length} slides</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid grid-cols-[220px_1fr] gap-4">
        {/* Folders sidebar */}
        <aside className="vp-card p-2 space-y-1 h-fit sticky top-4" data-testid="folders-sidebar">
          <button
            onClick={() => setActiveFolder("all")}
            className={"w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 " + (activeFolder === "all" ? "bg-[#7C3AED]/10 text-[#7C3AED]" : "hover:bg-[#F1F3F7]")}
            data-testid="folder-all"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> All decks <span className="ml-auto text-[10px] text-[#747680]">{items.length}</span>
          </button>
          <button
            onClick={() => setActiveFolder("uncat")}
            className={"w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 " + (activeFolder === "uncat" ? "bg-[#7C3AED]/10 text-[#7C3AED]" : "hover:bg-[#F1F3F7]")}
            data-testid="folder-uncat"
          >
            <Folder className="w-3.5 h-3.5" /> Uncategorised <span className="ml-auto text-[10px] text-[#747680]">{items.filter(p => !p.folder_id).length}</span>
          </button>
          <div className="border-t border-[#E4E6EC] my-1" />
          {folders.map((f) => (
            <div key={f.id} className="flex items-center group">
              <button
                onClick={() => setActiveFolder(f.id)}
                className={"flex-1 text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 " + (activeFolder === f.id ? "bg-[#7C3AED]/10 text-[#7C3AED]" : "hover:bg-[#F1F3F7]")}
                data-testid={`folder-${f.id}`}
              >
                <Folder className="w-3.5 h-3.5" style={{ color: f.color }} />
                <span className="truncate">{f.name}</span>
                <span className="ml-auto text-[10px] text-[#747680]">{f.count}</span>
              </button>
              <button onClick={() => delFolder(f.id)} className="opacity-0 group-hover:opacity-100 p-1 text-[#B00020] hover:bg-[#F1F3F7] rounded" title="Delete"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          <button onClick={createFolder} className="w-full text-left px-2 py-1.5 rounded text-xs text-[#7C3AED] hover:bg-[#F1F3F7] inline-flex items-center gap-1.5" data-testid="new-folder-btn">
            <FolderPlus className="w-3.5 h-3.5" /> New folder
          </button>
        </aside>

        {/* Decks grid */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-[#4A4D57]">Loading…</div>
          ) : filteredItems.length === 0 ? (
            <div className="vp-card p-10 text-center space-y-3">
              <PresIcon className="w-10 h-10 mx-auto text-[#7C3AED]" strokeWidth={1.5} />
              <div className="font-display font-bold text-xl">
                {activeFolder === "all" ? "Your first AI presentation is one click away" : "No decks in this folder yet"}
              </div>
              <div className="text-sm text-[#4A4D57]">Enter a topic, audience, and slide count — AI writes the whole deck.</div>
              <button onClick={() => nav("/app/presentations/new")} className="vp-btn-primary inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Create your first deck
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((p) => (
                <div key={p.id} className="vp-card p-0 overflow-hidden group" data-testid={`pres-card-${p.id}`}>
                  <div
                    onClick={() => nav(`/app/presentations/${p.id}/edit`)}
                    className="cursor-pointer h-32 bg-gradient-to-br from-[#7C3AED] to-[#0B5CFF] p-4 flex flex-col justify-end"
                  >
                    <div className="text-white font-display font-bold text-sm line-clamp-2">{p.title}</div>
                    <div className="text-white/70 text-[10px] mt-0.5">{p.subtitle}</div>
                  </div>
                  <div className="p-3 space-y-1.5">
                    <div className="flex items-center gap-2 text-xs text-[#4A4D57]">
                      <FileText className="w-3 h-3" /> {p.slide_count} slides
                      <span className="mx-1">•</span>
                      <Calendar className="w-3 h-3" /> {new Date(p.edited_at).toLocaleDateString()}
                    </div>
                    <div className="text-[10px] text-[#747680] truncate">{p.presentation_type} · {p.language}</div>
                    <select
                      className="vp-input py-1 px-1.5 text-[10px] w-full"
                      value={p.folder_id || ""}
                      onChange={(e) => assignFolder(p.id, e.target.value || null)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`folder-select-${p.id}`}
                    >
                      <option value="">— Uncategorised —</option>
                      {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                    <div className="flex gap-1 pt-1">
                      <button onClick={() => nav(`/app/presentations/${p.id}/edit`)} className="vp-btn-primary text-xs py-1.5 px-3 flex-1">Open</button>
                      <button onClick={() => download(p.id, p.title)} className="vp-btn-ghost text-xs py-1.5 px-2" title="Download PPTX" data-testid={`download-${p.id}`}><Download className="w-3.5 h-3.5" /></button>
                      <button onClick={() => duplicate(p.id)} className="vp-btn-ghost text-xs py-1.5 px-2" title="Duplicate"><Copy className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(p.id)} className="vp-btn-ghost text-xs py-1.5 px-2 text-[#B00020]" title="Delete" data-testid={`delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
